import { observer } from "mobx-react-lite";
import { Form as FinalForm, Field } from "react-final-form";
import {
  isRequired,
  combineValidators,
  composeValidators,
  isNumeric,
  hasLengthBetween,
} from "revalidate";
import React, { useContext } from "react";
import { Alert, Button, FormGroup, Form } from "reactstrap";
import { FORM_ERROR } from "final-form";
import TextInput from "../common/TextInput";
import { RootStoreContext } from "../../app/stores/rootStore";
import { INativeAppConf, ISetPinFormValues } from "../../app/models/offline";
import { Redirect } from "react-router-dom";
import LogoCompany from "../common/LogoCompany";
import { useTranslation } from "react-i18next";
import { translationHookOptions } from "../../app/localization/localization";

const validate = combineValidators({
  pin: composeValidators(isRequired, isNumeric, hasLengthBetween(4, 6))("pin"),
});

interface IParams {
  configuration: INativeAppConf;
}

const OfflinePinForm: React.FC<IParams> = ({ configuration }) => {
  const rootStore = useContext(RootStoreContext);
  const { isOnline } = rootStore.commonStore;
  const { validateOfflinePin, isTokenValidOffline } = rootStore.offlineStore;
  const [t] = useTranslation(["nativeapp", "translation"], translationHookOptions);

  if (!configuration.requirePin || isTokenValidOffline)
    return <Redirect to="/home" />;

  return (
    <div className="form-wrapper  mt-0">
      <div>
        <LogoCompany className="loginLogo" />
      </div>
      <h4 className="mb-3">{t("navbar_dropdown_offline_mode")}</h4>
      <h6>{t("insert_pin")}</h6>
      <FinalForm
        initialValues={{
          pin: "",
        }}
        onSubmit={(values: ISetPinFormValues) => {
          try {
            validateOfflinePin(parseInt(values.pin));
          } catch (error) {
            return { [FORM_ERROR]: error };
          }
        }}
        validate={validate}
        render={({
          handleSubmit,
          submitting,
          submitError,
          invalid,
          pristine,
          dirtySinceLastSubmit,
        }) => (
          <Form onSubmit={handleSubmit} role="form">
            <FormGroup className="form-group input-group-lg">
              <Field
                className="form-control"
                name="pin"
                component={TextInput}
                placeholder="PIN"
                type="password"
                autoComplete="off"
              />
            </FormGroup>
            {submitError && !dirtySinceLastSubmit && (
              <Alert color="danger">{t("pin_error")}</Alert>
            )}
            <div className="text-center">
              <Button
                disabled={(invalid && !dirtySinceLastSubmit) || pristine}
                className="btn loginButton btn-block btn-lg"
                color="dark"
                type="submit"
              >
                {t("login", { ns : "translation" })}
              </Button>
            </div>
          </Form>
        )}
      />
      {isOnline && (
        <>
          <div className="text-center text-muted mb-0">
            <small>{t("login_method_separator", { ns : "translation" })}</small>
          </div>
          <div className="text-center text-muted mb-4">
            <a
              className="btn btn-dark text-white loginButton btn-outline-light btn-sm"
              href="/"
            >
              <u>{t("go_online")}</u>
            </a>
          </div>
        </>
      )}
    </div>
  );
};

export default observer(OfflinePinForm);
